import { useEffect, useRef, useState } from 'react';
import { useGameStore } from '@/state/gameStore';

interface AnimatedCounterProps {
  value: number;
  /** Animation duration in ms. Default 1500. Reduced when prefers-reduced-motion. */
  durationMs?: number;
  /** Optional formatter (default: integer with German thousand separators). */
  format?: (n: number) => string;
}

/**
 * Smoothly animates an integer counter from its previous value to `value`
 * using easeOutCubic over `durationMs`. Falls back to instant updates when
 * the difference is small (< 1) or when reduceMotion is on.
 */
export function AnimatedCounter({
  value,
  durationMs = 1500,
  format = (n) => Math.round(n).toLocaleString('de-DE'),
}: AnimatedCounterProps) {
  const reduceMotion = useGameStore((s) => s.uiPrefs.reduceMotion);
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const startRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastTargetRef = useRef(value);

  useEffect(() => {
    if (lastTargetRef.current === value) return;
    lastTargetRef.current = value;

    const from = display;
    const to = value;
    const diff = to - from;
    if (Math.abs(diff) < 1 || reduceMotion) {
      setDisplay(to);
      return;
    }

    fromRef.current = from;
    startRef.current = null;

    const dur = Math.max(150, durationMs);

    const step = (now: number) => {
      if (startRef.current === null) startRef.current = now;
      const t = Math.min(1, (now - startRef.current) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = fromRef.current + (to - fromRef.current) * eased;
      setDisplay(current);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    };
    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, durationMs, reduceMotion]);

  return <>{format(display)}</>;
}
